import { analyzeCapacity } from "./core/capacity"
import type { CapacityReport } from "./core/capacity"
import { blocksPerColorway, alternateBlocksPerColorway, blocksTotal, sortedGroups, sashingPlan, cornerstonePlan, settingTrianglePlan, borderPlan, blockFinishedInches, stripPlan, quiltFinishedInches, sandwichPlan } from "./core/geometry"
import type { SandwichPlan } from "./core/geometry"
import { findFabricByName, findFabricByRole, findFabricBySlot, makeFabric } from "./core/state"
import { alternateBlock, blockForSchema, maxDistinctGroupsFor, distributionLabel } from "./core/layout"
import { assertNever, biasEdgeCount, cutSize, cutLength, defaultPieceRolesFor, faceLabelForGrid, faceRoleKeys, getAssemblyHints, getSchema, patternFamily, registeredSchemaNames, schemaSlots, schemaLabel } from "./core/blockschema"
import type { ToolName } from "./tool-schemas"
import { assemblySections } from "./core/assembly"
import { renderBlock, renderBlockDiagram, DEFAULT_BLOCK_COLORS, toValueColors } from "./core/block_svg"
import { fullQuiltSvg, fabricSwatch } from "./core/layout"
import { DIMENSIONS } from "./core/dimensions"
import { computeUsage } from "./core/yardage"
import { inFraction, fmtYards } from "./core/plan_utils"
import type { StructuralRole, Project, Fabric, FabricValue } from "./core/state"
import { makeBlockArtifact, makeQuiltArtifact, type BlockArtifact, type QuiltArtifact } from "./artifact"
export type { BlockArtifact, QuiltArtifact, Artifact, RenderedBlock, RenderedQuilt, RenderedThumbnail } from "./artifact"

// ── Client-side executors ─────────────────────────────────────────────────────

// Single source of truth for menu-icon names. The TS union is derived from
// this tuple; the Lucide component map (ChatPane) keys off the union and
// fails to compile if an entry is missing; the zod schema (tool-schemas)
// imports the tuple directly. Adding an icon is one append here — TS catches
// the missing component, the LLM sees the new name immediately.
export const MENU_ICON_NAMES = [
  "add", "edit", "remove",
  "block", "quilt", "layout",
  "cut", "plan", "colors", "name",
  "measure", "fabric",
  "info", "surprise", "random",
  "print", "play", "next",
] as const
export type MenuIconName = typeof MENU_ICON_NAMES[number]

// A deterministic tool-call request the menu can synthesize without an LLM
// round-trip. text is the verbatim user-message text shown in the transcript;
// toolName + args are what the synthesizer feeds into executeTool. Optional
// `chain` runs follow-up tool calls in the same synthesized assistant turn —
// each sees the project state mutated by the previous call. Used for "click a
// swatch → colorway_set + render_block in one transcript event."
export interface MenuSynthRequest {
  text: string
  toolName: ToolName
  args: Record<string, unknown>
  chain?: Array<{ toolName: ToolName; args: Record<string, unknown> }>
}

export interface MenuOption {
  text: string
  icon?: MenuIconName
  swatches?: string[]
  children?: MenuOption[]  // clicking opens a sub-menu instead of sending text
  onSelect?: () => void    // client-side action; overrides sending text
  // When set, tapping synthesizes a tool call into the transcript — same
  // path the LLM uses, executed deterministically. Takes precedence over
  // onSelect and over send-text-on-tap.
  synth?: MenuSynthRequest
  // Slot-row marker for the Fabrics → Colorway submenu. The renderer swaps
  // the row's <button> for a <label> wrapping a hidden <input type="color">
  // (same primitive as BlockColorSwatches), and on `change` ChatPane
  // dispatches colorway_set + render_block via handleColorChange. Carries
  // data only — no callback — so options are still serializable.
  colorPicker?: { slot: string; current: string }
  // When set, the row renders disabled with this reason inline. Suppresses
  // synth/onSelect/children navigation. Used for value-level applicability
  // gating (e.g. axis-swapping distributions on non-square blocks).
  disabledReason?: string
}

export interface ToolResult {
  result: unknown
  updatedProject?: Project
}

// Tools whose results are consumed by client-side side effects (inline
// visualization or UI state). The shape of each entry is enforced at the
// producer site via `satisfies ClientRenderResults["…"]` and at the
// consumer site (ChatPane) via a typed handler registry — adding a new
// entry here forces both ends to stay in sync.
export interface ClientRenderResults {
  render_block: { svg: string; artifact: BlockArtifact }
  render_block_diagram: { svg: string; artifact: BlockArtifact }
  render_quilt: { svg: string; artifact: QuiltArtifact } | { error: string }
  render_quilt_grid: { thumbnails: { svg: string; label: string; artifact: QuiltArtifact }[] } | { error: string }
  render_plan: { markdown: string }
  show_menu: { ok: true; options: MenuOption[] }
}

export type ClientRenderTool = keyof ClientRenderResults

// Typed shapes for non-render tool outputs. Exported so ChatPane's ToolVisual
// can import them directly instead of re-declaring local shapes — a rename here
// fails to compile at the consumer rather than silently returning null.
export type UsageRow = { fabric: string; have_inches: number; need_inches: number; headroom_inches: number; ok: boolean }
export type FabricRow = { name: string; role: string | null; slot: string | null; group: string | null; yards: number; swatch?: string }
export type PieceRow = {
  name: string; kind: string; size_units: number; cut_size: number; cut_length: number;
  count_per_block: number; total_count: number; fabric_slot: string;
  fabric_by_group?: Record<string, string>; groups_covered: string[]; missing_groups: string[];
  strip_plan?: { pieces_per_strip: number; strips_per_group: number; inches_per_group: number }
}
export type BlockInfoOut = {
  style: string; unit_inches: number; seam: number;
  grid_units: [number, number]; finished_inches: [number, number];
  total_blocks: number; groups: number; blocks_per_group: number;
  family?: string; pieces: PieceRow[]
}
export type BlockOut = { style: string; unit_inches: number; seam: number; piece_roles: Record<string, string> }
export type LayoutOut = { rows: number; cols: number; distribution: string; sashing_width?: number }
export type CellOut = { r: number; c: number; style: string | null; rows: number; cols: number; cell_schemas: Record<string, string> }
export type { SandwichPlan, CapacityReport }


// Schema-default fallback colors for the active block's slot keys. Used as
// the underlay for both per-group colorway resolution and preview rendering.
function slotDefaultColors(project: Project): Record<string, string> {
  const roles = schemaSlots(getSchema(project.block.style))
  return Object.fromEntries(roles.map((r) => [r, DEFAULT_BLOCK_COLORS[r] ?? "#888888"]))
}

// Schema defaults overlaid with a colorway group's overrides. The single
// answer to "what hex should slot X show right now?" — used by the
// Fabrics → Colorway submenu's swatches so the picker reflects state even
// before any block has rendered, and by previewColors below for the render
// path. Pass an unknown group (or undefined) to get just the defaults.
export function effectiveColorway(
  project: Project,
  group?: string,
): Record<string, string> {
  const overrides = group ? project.colorways[group] ?? {} : {}
  return { ...slotDefaultColors(project), ...overrides }
}

// First group key for preview rendering. Prefers fabric-derived groups so
// fabric ordering wins once fabrics exist; falls back to colorway keys so a
// colorway_set call before any fabric_add still lands in the rendered block.
// Without this fallback, render_block silently ignores user-set hexes whenever
// the model writes a colorway without first adding a matching fabric group.
function firstColorwayGroup(project: Project): string | undefined {
  const fabricGroups = sortedGroups(project)
  if (fabricGroups.length > 0) return fabricGroups[0]
  const colorwayGroups = Object.keys(project.colorways).sort()
  return colorwayGroups[0]
}

// Preview palette for single-block renders. Returns three things:
// - `rendered`: what to paint with (greyscale when mode="value")
// - `design`: the original colorway hexes — what goes into the artifact
// - `group`: the source colorway group key, or undefined when no groups exist
// Single source of truth for "first group + defaults filled in" so the
// rendered SVG and the pinned artifact stay in lockstep.
function previewColors(
  project: Project,
  mode?: string,
): { rendered: Record<string, string>; design: Record<string, string>; group: string | undefined } {
  const group = firstColorwayGroup(project)
  const design = effectiveColorway(project, group)
  const rendered = mode === "value" ? toValueColors(design) : design
  return { rendered, design, group }
}


export function executeTool(
  toolName: ToolName,
  args: Record<string, unknown>,
  project: Project
): ToolResult {
  switch (toolName) {
    case "project_set": {
      const updated = { ...project, name: args.name as string }
      return { result: { ok: true, name: args.name }, updatedProject: updated }
    }

    case "fabric_add": {
      type FabricSpec = { name: string; role?: StructuralRole | null; slot?: string | null; group?: string | null; yards: number; width?: number; value?: FabricValue; hex?: string | null }
      const batchSpecs = args.fabrics as FabricSpec[] | undefined
      const specs: FabricSpec[] = batchSpecs ?? [{
        name: args.name as string,
        role: (args.role as StructuralRole | undefined) ?? null,
        slot: (args.slot as string | undefined) ?? null,
        group: (args.group as string | null) ?? null,
        yards: args.yards as number,
        width: args.width as number | undefined,
        ...(args.value !== undefined ? { value: args.value as FabricValue } : {}),
        ...(args.hex !== undefined ? { hex: args.hex as string } : {}),
      }]

      const added: object[] = []
      const errors: string[] = []
      let updated = project
      for (const spec of specs) {
        if (updated.fabrics.some((f) => f.name === spec.name)) {
          errors.push(`fabric '${spec.name}' already exists`)
          continue
        }
        let fabric: Fabric
        try {
          fabric = makeFabric(spec)
        } catch (e) {
          errors.push((e as Error).message)
          continue
        }
        updated = { ...updated, fabrics: [...updated.fabrics, fabric] }
        added.push({ ...fabric, swatch: fabricSwatch(updated, fabric) })
      }

      if (!batchSpecs) {
        return added.length > 0
          ? { result: { ok: true, fabric: added[0] }, updatedProject: updated }
          : { result: { ok: false, error: errors[0] } }
      }
      return { result: { ok: errors.length === 0, added, errors }, updatedProject: updated }
    }

    case "fabric_update": {
      const name = args.name as string
      // role/slot are mutually exclusive — passing one rebuilds the fabric in
      // that mode (clearing the other); passing both is an error; passing
      // neither leaves the discriminator alone.
      const roleProvided = "role" in args && args.role !== undefined
      const slotProvided = "slot" in args && args.slot !== undefined
      if (roleProvided && slotProvided) {
        return { result: { ok: false, error: "fabric_update: role and slot are mutually exclusive" } }
      }
      const fabrics = project.fabrics.map((f): Fabric => {
        if (f.name !== name) return f
        const merged = {
          name: f.name,
          group: "group" in args ? (args.group as string | null) : f.group,
          yards: "yards" in args && args.yards !== undefined ? (args.yards as number) : f.yards,
          width: "width" in args && args.width !== undefined ? (args.width as number) : f.width,
          value: "value" in args && args.value !== undefined ? (args.value as FabricValue) : f.value,
          hex: "hex" in args && args.hex !== undefined ? (args.hex as string) : f.hex,
        }
        const role = roleProvided ? (args.role as StructuralRole) : slotProvided ? null : f.role
        const slot = slotProvided ? (args.slot as string) : roleProvided ? null : f.slot
        return makeFabric({ ...merged, role, slot })
      })
      const updated = { ...project, fabrics }
      const changed = findFabricByName(updated, name)
      return {
        result: { ok: !!changed, fabric: changed ? { ...changed, swatch: fabricSwatch(updated, changed) } : undefined },
        updatedProject: updated,
      }
    }

    case "fabric_remove": {
      const name = args.name as string
      if (!project.fabrics.some((f) => f.name === name)) {
        return { result: { ok: false, error: `Fabric '${name}' not found` } }
      }
      const fabrics = project.fabrics.filter((f) => f.name !== name)
      return { result: { ok: true, removed: name }, updatedProject: { ...project, fabrics } }
    }

    case "fabric_list": {
      const fabrics = project.fabrics.map((f) => ({ ...f, swatch: fabricSwatch(project, f) }))
      return { result: { fabrics } }
    }

    case "block_set": {
      const nextStyle = (args.style as string | undefined) ?? project.block.style
      const nextSchema = getSchema(nextStyle)

      // Reject piece_roles keys that aren't patch names in the target schema.
      if (args.piece_roles) {
        const validNames = nextSchema.patches.map((p) => p.name)
        const invalidKeys = Object.keys(args.piece_roles as Record<string, string>).filter(
          (k) => !validNames.includes(k)
        )
        if (invalidKeys.length > 0) {
          return {
            result: {
              ok: false,
              error: `Invalid piece_roles key(s): ${invalidKeys.join(", ")}. Valid patch names for ${nextStyle}: ${validNames.join(", ")}`,
            },
          }
        }
      }

      // Switching style replaces the patch name set, so reset piece_roles to
      // the new schema's defaults (then layer any explicit overrides on top).
      const styleChanged = nextStyle !== project.block.style
      const baseRoles = styleChanged
        ? defaultPieceRolesFor(nextSchema)
        : project.block.piece_roles
      const block = {
        ...project.block,
        style: nextStyle,
        ...(args.unit_inches !== undefined ? { unit_inches: args.unit_inches as number } : {}),
        ...(args.seam !== undefined ? { seam: args.seam as number } : {}),
        piece_roles: args.piece_roles
          ? { ...baseRoles, ...(args.piece_roles as Record<string, string>) }
          : baseRoles,
      }
      const updated = { ...project, block }
      const [finishedW, finishedH] = blockFinishedInches(block)
      return {
        result: {
          ok: true,
          block,
          style_label: schemaLabel(block.style),
          finished_inches: [finishedW, finishedH],
        },
        updatedProject: updated,
      }
    }

    case "layout_set": {
      if (args.alternate_schema !== undefined && args.alternate_schema !== null) {
        const altName = args.alternate_schema as string
        if (!registeredSchemaNames().includes(altName)) {
          return { result: { ok: false, error: `Unknown schema '${altName}'. Valid schemas: ${registeredSchemaNames().join(", ")}` } }
        }
      }
      const layout = {
        ...project.layout,
        ...(args.rows !== undefined ? { rows: args.rows as number } : {}),
        ...(args.cols !== undefined ? { cols: args.cols as number } : {}),
        ...(args.distribution !== undefined ? { distribution: args.distribution as import("./core/state").Distribution } : {}),
        ...(args.setting !== undefined ? { setting: args.setting as import("./core/state").Setting } : {}),
        ...(args.border_h_inches !== undefined ? { border_h_inches: args.border_h_inches as number } : {}),
        ...(args.border_v_inches !== undefined ? { border_v_inches: args.border_v_inches as number } : {}),
        ...(args.alternate_schema !== undefined
          ? { alternate_schema: args.alternate_schema === null ? undefined : (args.alternate_schema as string) }
          : {}),
        ...(args.alternate_piece_roles !== undefined
          ? { alternate_piece_roles: args.alternate_piece_roles === null ? undefined : (args.alternate_piece_roles as Record<string, string>) }
          : {}),
      }
      const updated = { ...project, layout }
      const max_distinct_groups = maxDistinctGroupsFor(layout.distribution, layout.rows, layout.cols)
      return { result: { ok: true, layout, max_distinct_groups }, updatedProject: updated }
    }

    case "colorway_set": {
      const group = args.group as string
      const existing = project.colorways[group] ?? {}
      const incoming = (args.colors as Record<string, string>) ?? {}
      const colorways = { ...project.colorways, [group]: { ...existing, ...incoming } }
      const updated = { ...project, colorways }
      return { result: { ok: true, group, colors: colorways[group] }, updatedProject: updated }
    }

    case "colorway_list": {
      const defaults = slotDefaultColors(project)
      const groups = sortedGroups(project)
      const colorways = Object.fromEntries(
        groups.map((g) => [g, { ...defaults, ...(project.colorways[g] ?? {}) }])
      )
      return { result: { colorways } }
    }

    case "capacity": {
      try {
        const groups = sortedGroups(project)
        if (groups.length === 0) return { result: { error: "No colorway groups defined" } }
        const report = analyzeCapacity(project)
        return { result: report }
      } catch (e) {
        return { result: { error: String(e) } }
      }
    }

    case "analyze": {
      const usages = computeUsage(project)
      const allOk = usages.every((u) => u.ok)
      return {
        result: {
          ok: allOk,
          total_blocks: blocksTotal(project),
          blocks_per_group: blocksPerColorway(project),
          usages: usages.map((u) => ({
            fabric: u.fabric,
            have_inches: Math.round(u.haveInches * 10) / 10,
            need_inches: Math.round(u.needInches * 10) / 10,
            headroom_inches: Math.round(u.headroomInches * 10) / 10,
            ok: u.ok,
          })),
        },
      }
    }

    case "block_info": {
      const schema = getSchema(project.block.style)
      const [finishedW, finishedH] = blockFinishedInches(project.block)
      const total = blocksTotal(project)
      const groups = sortedGroups(project)
      const bpg = blocksPerColorway(project)
      const pieceRoles = project.block.piece_roles
      const pieces = schema.patches.map((p) => {
        const fabricSlot = pieceRoles[p.name] ?? p.default_role
        const matches = project.fabrics.filter((f) => f.slot === fabricSlot && f.group)
        const groupsCovered = matches.map((f) => f.group as string).sort()
        const missingGroups = groups.filter((g) => !groupsCovered.includes(g))
        const fabricByGroup: Record<string, string> = {}
        for (const m of matches) if (m.group) fabricByGroup[m.group] = m.name
        const cs = cutSize(p, project.block)
        const cl = cutLength(p, project.block)
        const plan = stripPlan({ fabric: "", patchName: p.name, size: cs, length: cl, count: p.count * bpg })
        return {
          name: p.name,
          kind: p.kind,
          size_units: p.size_units,
          cut_size: cs,
          cut_length: cl,
          count_per_block: p.count,
          total_count: p.count * total,
          fabric_slot: fabricSlot,
          fabric_by_group: fabricByGroup,
          groups_covered: groupsCovered,
          missing_groups: missingGroups,
          strip_plan: { pieces_per_strip: plan.squaresPerStrip, strips_per_group: plan.strips, inches_per_group: plan.inchesUsed },
        }
      })
      const altInfo = project.layout.alternate_schema
        ? (() => {
            const altBlock = alternateBlock(project)
            const altSchema = getSchema(altBlock.style)
            const altBpg = alternateBlocksPerColorway(project)
            const altPieces = altSchema.patches.map((p) => {
              const fabricSlot = altBlock.piece_roles[p.name] ?? p.default_role
              const cs = cutSize(p, altBlock)
              const cl = cutLength(p, altBlock)
              const plan = stripPlan({ fabric: "", patchName: p.name, size: cs, length: cl, count: p.count * altBpg })
              return {
                name: p.name,
                kind: p.kind,
                cut_size: cs,
                cut_length: cl,
                count_per_block: p.count,
                total_count: p.count * Math.floor(total / 2),
                fabric_slot: fabricSlot,
                strip_plan: { pieces_per_strip: plan.squaresPerStrip, strips_per_group: plan.strips, inches_per_group: plan.inchesUsed },
              }
            })
            const [altW, altH] = blockFinishedInches(altBlock)
            return { style: altBlock.style, grid_units: altSchema.grid_units, finished_inches: [altW, altH], blocks_per_group: altBpg, pieces: altPieces }
          })()
        : undefined
      const biasCount = biasEdgeCount(schema)
      return {
        result: {
          style: schema.name,
          unit_inches: project.block.unit_inches,
          seam: project.block.seam,
          grid_units: schema.grid_units,
          finished_inches: [finishedW, finishedH],
          total_blocks: total,
          groups: groups.length,
          blocks_per_group: bpg,
          pieces,
          family: patternFamily(schema),
          bias_edge_count: biasCount,
          bias_note: biasCount > 0
            ? `${biasCount} bias-edge face(s); starch or spray stabilizer recommended before cutting`
            : null,
          ...(altInfo ? { alternate: altInfo } : {}),
        },
      }
    }

    case "render_block": {
      const scale = (args.scale as number | undefined) ?? 60
      const { rendered, design, group } = previewColors(project, args.mode as string | undefined)
      const svg = renderBlock(project.block, scale, rendered)
      const artifact = makeBlockArtifact(project, design, group)
      return { result: { svg, artifact } satisfies ClientRenderResults["render_block"] }
    }

    case "render_block_diagram": {
      const scale = (args.scale as number | undefined) ?? 60
      const { rendered, design, group } = previewColors(project, args.mode as string | undefined)
      const svg = renderBlockDiagram(project.block, scale, rendered)
      const artifact = makeBlockArtifact(project, design, group)
      return { result: { svg, artifact } satisfies ClientRenderResults["render_block_diagram"] }
    }

    case "render_quilt": {
      if (project.layout.rows === 0 || project.layout.cols === 0) {
        return { result: { error: "Layout has no rows/cols. Set the grid first." } satisfies ClientRenderResults["render_quilt"] }
      }
      const blockPx = (args.block_pixels as number | undefined) ?? 48
      const mode = (args.mode as string | undefined) ?? "color"
      const svg = fullQuiltSvg(project, blockPx, mode as "color" | "value")
      const artifact = makeQuiltArtifact(project)
      return { result: { svg, artifact } satisfies ClientRenderResults["render_quilt"] }
    }

    case "render_quilt_grid": {
      if (project.layout.rows === 0 || project.layout.cols === 0) {
        return { result: { error: "Layout has no rows/cols. Set the grid first." } satisfies ClientRenderResults["render_quilt_grid"] }
      }
      const dimensionId = args.dimension_id as string
      const dim = DIMENSIONS.find(d => d.id === dimensionId)
      if (!dim || !dim.values || dim.values.length === 0) {
        return { result: { error: `Dimension '${dimensionId}' not found or has no discrete values.` } satisfies ClientRenderResults["render_quilt_grid"] }
      }
      if (dim.applicability && !dim.applicability(project)) {
        return { result: { error: `Dimension '${dimensionId}' does not apply to the current project.` } satisfies ClientRenderResults["render_quilt_grid"] }
      }
      const rawValues = args.values as (string | number)[] | undefined
      const entries = rawValues
        ? rawValues.map(v => ({ value: v, label: dim.values!.find(dv => dv.value === v)?.label ?? String(v) }))
        : dim.values
      const blockPx = (args.block_pixels as number | undefined) ?? 32
      const thumbnails = entries.map(({ value, label }) => {
        const variant = dim.apply(project, value)
        return { svg: fullQuiltSvg(variant, blockPx), label, artifact: makeQuiltArtifact(variant) }
      })
      return { result: { thumbnails } satisfies ClientRenderResults["render_quilt_grid"] }
    }

    case "render_plan": {
      const plan = renderPlanMd(project)
      return { result: { markdown: plan } satisfies ClientRenderResults["render_plan"] }
    }

    case "sandwich_plan": {
      return { result: sandwichPlan(project) }
    }

    case "sashing_set": {
      const layout = { ...project.layout, sashing_width: args.width_inches as number }
      const updated = { ...project, layout }
      return { result: { ok: true, sashing_width: args.width_inches }, updatedProject: updated }
    }

    case "cell_set": {
      const r = args.r as number
      const c = args.c as number
      const style = args.style as string | null | undefined

      const { rows, cols } = project.layout
      if (rows <= 0 || cols <= 0) {
        return { result: { ok: false, error: "Set layout rows/cols first" } }
      }
      if (r < 0 || r >= rows || c < 0 || c >= cols) {
        return { result: { ok: false, error: `Cell (${r},${c}) out of bounds for ${cols}×${rows} layout` } }
      }
      if (style && !registeredSchemaNames().includes(style)) {
        return { result: { ok: false, error: `Unknown schema '${style}'. Valid: ${registeredSchemaNames().join(", ")}` } }
      }

      const key = `${r},${c}`
      const existing: Record<string, string> = { ...(project.layout.cell_schemas ?? {}) }
      if (!style) {
        delete existing[key]
      } else {
        existing[key] = style
      }
      const cell_schemas = Object.keys(existing).length > 0 ? existing : undefined
      const layout = { ...project.layout, cell_schemas }
      return {
        result: { ok: true, r, c, style: style ?? null, rows, cols, cell_schemas: cell_schemas ?? {} },
        updatedProject: { ...project, layout },
      }
    }

    case "value_audit": {
      const schema = getSchema(project.block.style)
      const pieceRoles = project.block.piece_roles

      // Resolve the fabric role(s) for each sub-region of a face
      function faceSubRoles(face: Parameters<typeof faceRoleKeys>[0]): string[] {
        return faceRoleKeys(face).map(key => {
          const patchName = face.roles[key]
          return pieceRoles[patchName] ?? schema.patches.find(p => p.name === patchName)?.default_role ?? patchName
        })
      }

      // True when two faces share an edge (not just a corner)
      function sharesEdge(a: { pos: [number,number], size: [number,number] }, b: { pos: [number,number], size: [number,number] }): boolean {
        const [ax, ay] = a.pos, [aw, ah] = a.size
        const [bx, by] = b.pos, [bw, bh] = b.size
        const vOverlap = Math.min(ay + ah, by + bh) > Math.max(ay, by)
        const hOverlap = Math.min(ax + aw, bx + bw) > Math.max(ax, bx)
        return (ax + aw === bx && vOverlap) || (bx + bw === ax && vOverlap) ||
               (ay + ah === by && hOverlap) || (by + bh === ay && hOverlap)
      }

      const pairSet = new Set<string>()

      // Intra-face: two sub-regions of the same face always share a seam
      for (const face of schema.faces) {
        const roles = faceSubRoles(face)
        if (roles.length === 2 && roles[0] !== roles[1]) {
          pairSet.add([roles[0], roles[1]].sort().join("|"))
        }
      }

      // Inter-face: faces that share an edge
      for (let i = 0; i < schema.faces.length; i++) {
        for (let j = i + 1; j < schema.faces.length; j++) {
          if (sharesEdge(schema.faces[i], schema.faces[j])) {
            for (const ra of faceSubRoles(schema.faces[i])) {
              for (const rb of faceSubRoles(schema.faces[j])) {
                if (ra !== rb) pairSet.add([ra, rb].sort().join("|"))
              }
            }
          }
        }
      }

      const adjacentPairs = [...pairSet].map(p => p.split("|") as [string, string])

      // Value annotation map: slot → first annotated fabric's value
      const slotValues: Record<string, string> = {}
      for (const f of project.fabrics) {
        if (f.value && f.slot && !(f.slot in slotValues)) slotValues[f.slot] = f.value
      }

      const conflicts: Array<{ slot_a: string, slot_b: string, value: string }> = []
      const unannotated = new Set<string>()
      for (const [ra, rb] of adjacentPairs) {
        const va = slotValues[ra], vb = slotValues[rb]
        if (!va) unannotated.add(ra)
        if (!vb) unannotated.add(rb)
        if (va && vb && va === vb) conflicts.push({ slot_a: ra, slot_b: rb, value: va })
      }

      return {
        result: {
          ok: conflicts.length === 0,
          slot_values: slotValues,
          adjacent_pairs: adjacentPairs,
          conflicts,
          unannotated: [...unannotated],
        },
      }
    }

    case "show_menu": {
      const options = (args.options as MenuOption[] | undefined) ?? []
      return { result: { ok: true, options } satisfies ClientRenderResults["show_menu"] }
    }

    default:
      return assertNever(toolName)
  }
}

// ── Inline plan_md renderer ───────────────────────────────────────────────────

function renderPlanMd(project: Project): string {
  const { block, layout, fabrics } = project
  const schema = getSchema(block.style)
  const groups = sortedGroups(project)
  const bpg = blocksPerColorway(project)
  const total = blocksTotal(project)
  const [blockW, blockH] = blockFinishedInches(block)
  const sashW = layout.sashing_width ?? 0
  const [quiltW, quiltH] = quiltFinishedInches(project)

  const lines: string[] = []
  lines.push(`# ${project.name} — Cut Plan`)
  lines.push("")
  const altSchemaName = layout.alternate_schema
  lines.push(`**Block:** ${schemaLabel(block.style)}, unit=${block.unit_inches}", seam=${inFraction(block.seam)}, finished=${blockW}"×${blockH}"`)
  if (altSchemaName) {
    lines.push(`**Alternating block:** ${schemaLabel(altSchemaName)} — odd-parity cells (checkerboard pattern)`)
  }
  lines.push(`**Layout:** ${layout.cols}×${layout.rows} = ${total} blocks${altSchemaName ? ` (${Math.ceil(total / 2)} ${schemaLabel(block.style)}, ${Math.floor(total / 2)} ${schemaLabel(altSchemaName)})` : `, ${groups.length} groups × ${bpg} blocks each`}`)
  lines.push(`**Distribution:** ${distributionLabel(layout.distribution)}`)
  lines.push(`**Setting:** ${layout.setting}`)
  if (sashW > 0) lines.push(`**Sashing:** ${inFraction(sashW)} finished`)
  lines.push(`**Quilt top:** ${inFraction(quiltW)} × ${inFraction(quiltH)}`)
  lines.push("")

  lines.push("## Fabrics")
  lines.push("")
  lines.push("| Fabric | Role/Slot | Group | Yards |")
  lines.push("|---|---|---|---|")
  for (const f of fabrics) {
    lines.push(`| ${f.name} | ${f.role ?? f.slot ?? "—"} | ${f.group ?? "—"} | ${f.yards} |`)
  }
  lines.push("")

  const usages = computeUsage(project)
  if (usages.length > 0) {
    lines.push("## Fabric Budget")
    lines.push("")
    lines.push("| Fabric | Have | Need | Headroom | OK |")
    lines.push("|---|---|---|---|---|")
    for (const u of usages) {
      const h = inFraction(u.haveInches)
      const n = inFraction(u.needInches)
      const hr = inFraction(u.headroomInches)
      lines.push(`| ${u.fabric} | ${h} | ${n} | ${hr} | ${u.ok ? "YES" : "NO"} |`)
    }
    lines.push("")
  }

  if (altSchemaName) {
    const altBlock = alternateBlock(project)
    const altSchema = getSchema(altBlock.style)
    const altBpg = alternateBlocksPerColorway(project)
    lines.push("## Alternating Blocks")
    lines.push("")
    lines.push(`Every other cell (odd-parity positions) is a **${schemaLabel(altSchemaName)}** instead of the primary block.`)
    lines.push(`${Math.floor(total / 2)} ${schemaLabel(altSchemaName)} blocks total; ~${altBpg} per colorway group.`)
    lines.push("")
    lines.push("| Piece | Cut size | Count/block | Count total |")
    lines.push("|---|---|---|---|")
    for (const p of altSchema.patches) {
      const cs = cutSize(p, altBlock)
      const cl = cutLength(p, altBlock)
      const sizeStr = cs === cl ? inFraction(cs) + " sq" : `${inFraction(cs)} × ${inFraction(cl)}`
      lines.push(`| ${p.label} (${p.default_role}) | ${sizeStr} | ${p.count} | ${p.count * Math.floor(total / 2)} |`)
    }
    lines.push("")
  }

  const cellSchemas = layout.cell_schemas
  if (cellSchemas && Object.keys(cellSchemas).length > 0) {
    const bySchema = new Map<string, string[]>()
    for (const [key, sName] of Object.entries(cellSchemas)) {
      if (!bySchema.has(sName)) bySchema.set(sName, [])
      bySchema.get(sName)!.push(key)
    }
    lines.push("## Cell Overrides")
    lines.push("")
    for (const [sName, keys] of bySchema) {
      const cellBlock = blockForSchema(project, sName)
      const cellSchema = getSchema(sName)
      lines.push(`### ${schemaLabel(sName)} — ${keys.length} cell${keys.length > 1 ? "s" : ""} (${keys.map(k => `(${k})`).join(", ")})`)
      lines.push("")
      lines.push("| Piece | Cut size | Count/block |")
      lines.push("|---|---|---|")
      for (const p of cellSchema.patches) {
        const cs = cutSize(p, cellBlock)
        const cl = cutLength(p, cellBlock)
        const sizeStr = cs === cl ? inFraction(cs) + " sq" : `${inFraction(cs)} × ${inFraction(cl)}`
        lines.push(`| ${p.label} (${p.default_role}) | ${sizeStr} | ${p.count} |`)
      }
      lines.push("")
    }
  }

  const sash = sashingPlan(project)
  if (sash !== null) {
    const sashFabric = findFabricByRole(project, "sashing")
    if (sashFabric) {
      lines.push("## Sashing")
      lines.push("")
      lines.push(`Width: ${inFraction(sashW)} finished / ${inFraction(sash.sashCut)} cut`)
      lines.push("")
      lines.push("| Cut | Size | Count | Strips | Per strip | WOF used |")
      lines.push("|---|---|---|---|---|---|")
      if (sash.vertPieceCut === sash.horizPieceCut) {
        lines.push(`| Sashing strips (${sashFabric.name}) | ${inFraction(sash.sashCut)} x ${inFraction(sash.vertPieceCut)} | ${sash.sashCount} | ${sash.sashStrips} | ${sash.vertPiecesPerStrip} | ${inFraction(sash.sashInches)} |`)
      } else {
        if (sash.vertCount > 0) {
          lines.push(`| Sashing vertical (${sashFabric.name}) | ${inFraction(sash.sashCut)} x ${inFraction(sash.vertPieceCut)} | ${sash.vertCount} | ${sash.vertStrips} | ${sash.vertPiecesPerStrip} | ${inFraction(sash.vertInches)} |`)
        }
        if (sash.horizCount > 0) {
          lines.push(`| Sashing horizontal (${sashFabric.name}) | ${inFraction(sash.sashCut)} x ${inFraction(sash.horizPieceCut)} | ${sash.horizCount} | ${sash.horizStrips} | ${sash.horizPiecesPerStrip} | ${inFraction(sash.horizInches)} |`)
        }
      }
      if (sash.cornerCount > 0) {
        const cornersPerStrip = Math.max(1, Math.floor(sashFabric.width / sash.cornerCut))
        lines.push(`| Cornerstones (${sashFabric.name}) | ${inFraction(sash.cornerCut)} sq | ${sash.cornerCount} | ${sash.cornerStrips} | ${cornersPerStrip} | ${inFraction(sash.cornerInches)} |`)
      }
      const cs = cornerstonePlan(project)
      if (cs !== null) {
        const csFabric = findFabricByRole(project, "cornerstone")
        if (csFabric) {
          lines.push(`| Cornerstones (${csFabric.name}) | ${inFraction(cs.cornerCut)} sq | ${cs.cornerCount} | ${cs.cornerStrips} | ${cs.piecesPerStrip} | ${inFraction(cs.cornerInches)} |`)
        }
      }
      lines.push("")
    }
  }

  const setting = settingTrianglePlan(project)
  if (setting !== null) {
    const settingFabric = findFabricByRole(project, "setting") ?? findFabricBySlot(project, "background")
    if (settingFabric) {
      lines.push("## Setting Triangles (on-point lattice)")
      lines.push("")
      lines.push(`Block diagonal: ${inFraction(setting.diagonal)} · Corner triangle leg: ${inFraction(setting.cornerLeg)}`)
      lines.push("")
      lines.push("| Cut | Size | Count | Yields | Strips | Per strip | WOF used |")
      lines.push("|---|---|---|---|---|---|---|")
      lines.push(`| Corner triangles (${settingFabric.name}) | ${inFraction(setting.cornerCutSquare)} sq | ${setting.cornerSquares} | ${setting.cornerCount} (2 per square) | ${setting.cornerStrips} | ${setting.cornerPerStrip} | ${inFraction(setting.cornerInches)} |`)
      lines.push("")
      lines.push("Cut each corner square once on the diagonal — yields 2 half-square triangles per square, legs on grain along the canvas perimeter. The four corner triangles fill the canvas-corner regions outside the inscribed lattice diamond.")
      if (!setting.fitsWof) {
        lines.push("")
        lines.push(`**Note:** corner cut square (${inFraction(setting.cornerCutSquare)}") exceeds your fabric width (${settingFabric.width}"). Piece the setting fabric from multiple cuts before cutting the diagonal.`)
      }
      lines.push("")
    }
  }

  const borders = borderPlan(project)
  if (borders !== null) {
    const settingFabric = findFabricByRole(project, "setting") ?? findFabricBySlot(project, "background")
    if (settingFabric) {
      lines.push("## Borders (on-point finish to rectangular blanket)")
      lines.push("")
      lines.push(`Pieced AABB: ${inFraction(borders.aabbSide)} sq · Finished: ${inFraction(borders.finishedW)} × ${inFraction(borders.finishedH)}`)
      lines.push("")
      lines.push("| Cut | Width | Length | Count |")
      lines.push("|---|---|---|---|")
      if (borders.sideStripCount > 0) {
        lines.push(`| Side borders (${settingFabric.name}) | ${inFraction(borders.sideStripWidth)} | ${inFraction(borders.sideStripLength)} | ${borders.sideStripCount} |`)
      }
      if (borders.tbStripCount > 0) {
        lines.push(`| Top/bottom borders (${settingFabric.name}) | ${inFraction(borders.tbStripWidth)} | ${inFraction(borders.tbStripLength)} | ${borders.tbStripCount} |`)
      }
      lines.push("")
      lines.push("Sew side borders to the pieced AABB first, then top/bottom borders across the full finished width. Press seams toward the borders. Piece any strip whose length exceeds your fabric WOF.")
      lines.push("")
    }
  }

  lines.push("## Assembly (per block)")
  lines.push("")
  for (const section of assemblySections(schema, block)) {
    lines.push(`### ${section.heading}`)
    lines.push("")
    section.steps.forEach((step, i) => {
      lines.push(`${i + 1}. ${step.main}`)
      for (const sub of step.subs) lines.push(`   - ${sub}`)
    })
    lines.push("")
  }
  const [cols, rows] = schema.grid_units
  // Strip-pieced faces span multiple grid cells, so the per-cell diagram is mostly
  // empty continuation slots and the row-sewing instruction contradicts the
  // strip-pieced section's cross-cut method. The Strip-pieced rails section
  // (emitted above) describes the correct construction on its own.
  const isStripPieced = getAssemblyHints(schema).includes("strip_pieced")
  if (!isStripPieced) {
    lines.push(`### Block assembly (${cols}×${rows} grid)`)
    lines.push("")
    lines.push("```")
    for (const row of asciiBlockGrid(schema)) lines.push(`  ${row}`)
    lines.push("```")
    lines.push("")
    if (rows > 2) {
      lines.push(`Sew rows 1–${rows} left-to-right. Press alternating rows in opposite directions so seams nest. Join rows.`)
    } else if (rows === 2) {
      lines.push("Sew each of the 2 rows left-to-right. Press seams in opposite directions so they nest, then join the rows.")
    } else if (cols > 1) {
      lines.push(`Sew the ${cols} patches in the row left-to-right.`)
    }
  }

  const sw = sandwichPlan(project)
  lines.push("")
  lines.push("## Sandwich")
  lines.push("")
  lines.push("### Binding")
  lines.push("")
  lines.push(`Cut strips 2½" × WOF.`)
  lines.push("")
  lines.push("| | |")
  lines.push("|---|---|")
  lines.push(`| Running inches | ${inFraction(sw.bindingRunningInches)} (includes joining + mitres) |`)
  lines.push(`| Strips needed | ${sw.bindingStripCount} |`)
  lines.push(`| Fabric needed | ${inFraction(sw.bindingFabricInches)} |`)
  lines.push(`| **Buy** | **${fmtYards(sw.bindingYards)}** |`)
  lines.push("")
  lines.push("### Backing")
  lines.push("")
  lines.push("| | |")
  lines.push("|---|---|")
  lines.push(`| Size needed | ${inFraction(sw.backingWidthNeeded)} × ${inFraction(sw.backingHeightNeeded)} |`)
  lines.push(`| Panels | ${sw.backingPanels} |`)
  lines.push(`| Fabric needed | ${inFraction(sw.backingFabricInches)} |`)
  lines.push(`| **Buy** | **${fmtYards(sw.backingYards)}** |`)
  if (sw.backingNote) {
    lines.push("")
    lines.push(`_${sw.backingNote}_`)
  }
  lines.push("")
  lines.push("### Batting")
  lines.push("")
  lines.push(`Coverage needed: ${inFraction(sw.battingWidthNeeded)} × ${inFraction(sw.battingHeightNeeded)}`)
  lines.push("")
  lines.push(sw.battingSize
    ? `Recommended: **${sw.battingSize}**`
    : "No standard batting size fits — order custom batting.")

  return lines.join("\n")
}

// One ASCII row per grid row, listing the patch label that lands in each
// cell. For flying-goose faces the row label is "goose ^/v/</>".
function asciiBlockGrid(schema: ReturnType<typeof getSchema>): string[] {
  const [cols, rows] = schema.grid_units
  const cells: string[][] = Array.from({ length: rows }, () => Array(cols).fill(""))
  for (const face of schema.faces) {
    const [px, py] = face.pos
    const [pw, ph] = face.size
    const label = faceLabelForGrid(face, schema)
    for (let dy = 0; dy < ph; dy++) {
      for (let dx = 0; dx < pw; dx++) {
        cells[py + dy][px + dx] = dx === 0 && dy === 0 ? label : ""
      }
    }
  }
  const colWidth = Math.max(8, ...cells.flat().map((c) => c.length)) + 2
  return cells.map((row) =>
    row.map((c) => `[${c.padEnd(colWidth - 2, " ")}]`).join(""),
  )
}

